import {
	buildOfficeAttachmentQueryVariables,
	type OfficeAttachmentQueryVariables,
} from '@confluence/office-macro-utils/entry-points/macroParamsUtils.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import {
	useViewXlsDataQuery,
	type useViewXlsDataQuery$data,
} from '@confluence/viewxls-macro/entry-points/useViewXlsDataQuery.ts';
import {
	parseViewXlsMacroParams,
	type ParsedViewXlsMacroParams,
} from '@confluence/viewxls-macro/entry-points/viewXlsMacroParamsUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

const buildVariables = (
	contentId: string,
	parsed: ParsedViewXlsMacroParams,
): OfficeAttachmentQueryVariables => {
	return buildOfficeAttachmentQueryVariables({
		contentId,
		pageName: parsed.pageName,
		spaceKey: parsed.spaceKey,
		date: parsed.date,
	});
};

/**
 * Preload handler for the viewxls
 *
 * For each extension node found in the ADF content, this handler
 * builds query variables from the macro parameters and fires the
 * useViewXlsDataQuery GraphQL query to populate the Relay store.
 */
export const viewXlsPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'viewxls',

	// viewxls is rendered in view, space-overview, and edit routes.
	// It is not rendered in live (live edit) routes.
	routes: ['view', 'space-overview', 'edit'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const macroParams = node.attrs?.parameters?.macroParams as Record<string, unknown> | undefined;
		const parsed = parseViewXlsMacroParams(macroParams);

		if (!parsed.attachmentName) {
			return;
		}

		const variables = buildVariables(context.contentId, parsed);

		await slowQueryGuard(
			'viewXlsPreload',
			() =>
				query<useViewXlsDataQuery$data, OfficeAttachmentQueryVariables>({
					query: useViewXlsDataQuery,
					variables,
					context: { single: true },
				}),
			SLOW_QUERY_DEFAULT_TIMEOUT,
		);
	},
};
